import React, {useCallback} from "react";
import {
	FilterWrapper,
	AccordionWrapper,
	useStyles
} from "./ProductFilter.style";
import {Form, Spinner} from "react-bootstrap";
import {
	Close as CloseIcon,
	Tune as TuneIcon,
} from "@material-ui/icons";
import {
	Button,
	Checkbox,
	FormControlLabel,
	Typography,
	Box,
	Divider,
	TextField,
	Chip,
	IconButton,
	Zoom,
} from "@material-ui/core";
import Switch from "react-switch";
import t from "../../../i18n/translate";
import FlagIconFactory from "react-flag-icon-css";
import {useFilters} from "./hooks/useFilters";
import {useAccordion} from "./hooks/useAccordion";
import {useExpandableList} from "./hooks/useExpandableList";
import {useFilterOptions} from "./hooks/useFilterOptions";
import FilterList from "./FilterList";
import AccordionItem from "./AccordionItem";

const FlagIcon = FlagIconFactory(React, {useCssModules: false});

const ITEMS_PER_PAGE = 10;

const ProductFilters = ({
	                        filterOptions,
	                        applyFilter,
	                        showFilter,
	                        toggleFilter,
	                        resetFilters,
	                        loading,
	                        initialFilters = {},
                        }) => {
	const classes = useStyles();

	// Custom hooks
	const {
		filters,
		toggleSelection,
		isSelected,
		updateFilter,
		updateFilters,
		clearFilters,
		activeCount,
	} = useFilters(initialFilters, filterOptions);

	const {accordions, toggle: toggleAccordion, isOpen} = useAccordion({
		category: true,
		subCategory: true,
		brand: true,
		dietary: true,
		country: true,
		promotion: true,
		outOfStock: true,
		sort: true,
		priceRange: true,
	});

	const expandableList = useExpandableList(ITEMS_PER_PAGE);
	const {sortedLists, sortOptions} = useFilterOptions(filterOptions);

	// Handlers
	const handleApplyFilter = useCallback(() => {
		applyFilter(filters);
		if (toggleFilter) toggleFilter();
	}, [filters, applyFilter, toggleFilter]);

	const handleClearFilters = useCallback(() => {
		clearFilters();
		expandableList.reset();
		if (resetFilters) resetFilters();
	}, [clearFilters, expandableList, resetFilters]);

	const handlePriceInput = useCallback((value, type) => {
		const inputValue = value.replace(/[^0-9]/g, '');
		const key = type === 'min' ? 'minPrice' : 'maxPrice';
		const defaultValue = type === 'min' ? 0 : 1000;
		updateFilter(key, inputValue || defaultValue);
	}, [updateFilter]);

	// Render checkbox item
	const renderCheckboxItem = useCallback((item, type, extraLabel = null) => (
		<FormControlLabel
			control={
				<Checkbox
					checked={isSelected(type, item.value)}
					onChange={() => toggleSelection(type, item)}
					color="primary"
					size="small"
					className={classes.checkboxRoot}
				/>
			}
			label={extraLabel || item.label}
			className={classes.checkboxLabel}
		/>
	), [isSelected, toggleSelection, classes]);

	// Render accordion with filter list
	const renderFilterAccordion = (title, key, items, type, customRenderer = null) => {
		if (!items?.length) return null;

		return (
			<AccordionItem
				title={title}
				isOpen={isOpen(key)}
				onToggle={() => toggleAccordion(key)}
				classes={classes}
			>
				<FilterList
					items={items}
					type={type}
					expandableList={expandableList}
					renderItem={customRenderer || ((item) => renderCheckboxItem(item, type))}
					classes={classes}
				/>
			</AccordionItem>
		);
	};

	return (
		<FilterWrapper>
			<AccordionWrapper>
				{/* Header */}
				<Box className={classes.header}>
					<Box className={classes.headerTitle}>
						<TuneIcon color="action"/>
						<Typography variant="h6" color="textSecondary">
							Filter By
						</Typography>
						{activeCount > 0 && (
							<Zoom in={true}>
								<Chip
									label={activeCount}
									size="small"
									color="primary"
									className={classes.activeFiltersChip}
								/>
							</Zoom>
						)}
					</Box>
					<IconButton onClick={toggleFilter} size="small">
						<CloseIcon/>
					</IconButton>
				</Box>

				{/* Body */}
				<Box className={classes.body}>
					{/* Category */}
					{renderFilterAccordion("Category Type", "category", sortedLists.categories, 'categories')}

					{/* Sub Category */}
					{renderFilterAccordion("Sub Category Type", "subCategory", sortedLists.subcategories, 'subcategories')}

					{/* Brand */}
					{renderFilterAccordion("Brand", "brand", sortedLists.brands, 'brands')}

					{/* Dietary */}
					{renderFilterAccordion("Dietary", "dietary", sortedLists.dietary, 'dietary')}

					{/* Country */}
					{renderFilterAccordion(
						"Country",
						"country",
						sortedLists.countries,
						'countries',
						(country) => renderCheckboxItem(country, 'countries', (
							<Box className={classes.countryFlag}>
								<FlagIcon code={country.code?.toLowerCase()} className="shadow"/>
								<span>{country.label}</span>
							</Box>
						))
					)}

					<Divider/>

					{/* Promotion */}
					<AccordionItem
						title="Promotion"
						isOpen={isOpen("promotion")}
						onToggle={() => toggleAccordion("promotion")}
						classes={classes}
					>
						<Box className={classes.switchContainer}>
							<Typography variant="body2">
								{t('show_only_discounted_products')}
							</Typography>
							<Switch
								className={`react-switch ${filters.showPromotion ? "active" : ""}`}
								onChange={() => updateFilter('showPromotion', !filters.showPromotion)}
								checked={filters.showPromotion}
							/>
						</Box>
					</AccordionItem>

					{/* Show Out Of Stock */}
					<AccordionItem
						title="Show Out Of Stock"
						isOpen={isOpen("outOfStock")}
						onToggle={() => toggleAccordion("outOfStock")}
						classes={classes}
					>
						<Box className={classes.switchContainer}>
							<Typography variant="body2">
								{t("shop_item_show_out_of_stock_option_title")}
							</Typography>
							<Switch
								className={`react-switch ${filters.showOutOfStock ? "active" : ""}`}
								onChange={() => updateFilter('showOutOfStock', !filters.showOutOfStock)}
								checked={filters.showOutOfStock}
							/>
						</Box>
					</AccordionItem>

					{/* Sort */}
					<AccordionItem
						title="Sort"
						isOpen={isOpen("sort")}
						onToggle={() => toggleAccordion("sort")}
						classes={classes}
					>
						<Form.Group controlId="formCreateEditOS">
							<Form.Control
								as="select"
								value={filters.sort}
								onChange={(e) => updateFilter('sort', e.target.value)}
							>
								{sortOptions.map((option) => (
									<option key={option.id} value={option.value}>
										{option.label}
									</option>
								))}
							</Form.Control>
						</Form.Group>
					</AccordionItem>

					{/* Price Range */}
					<AccordionItem
						title="Price Range"
						isOpen={isOpen("priceRange")}
						onToggle={() => toggleAccordion("priceRange")}
						classes={classes}
					>
						<Box className={classes.priceInputs}>
							<TextField
								label="Min"
								type="number"
								variant="outlined"
								size="small"
								value={filters.minPrice}
								onChange={(e) => handlePriceInput(e.target.value, 'min')}
								onBlur={() => !filters.minPrice && updateFilter('minPrice', 0)}
								className={classes.priceInput}
								InputProps={{inputProps: {min: 0}}}
							/>
							<TextField
								label="Max"
								type="number"
								variant="outlined"
								size="small"
								value={filters.maxPrice}
								onChange={(e) => handlePriceInput(e.target.value, 'max')}
								onBlur={() => !filters.maxPrice && updateFilter('maxPrice', 1000)}
								className={classes.priceInput}
								InputProps={{inputProps: {min: 0}}}
							/>
						</Box>
					</AccordionItem>
				</Box>

				{/* Footer Buttons */}
				<Box className={classes.footer}>
					<Button
						variant="outlined"
						className={classes.clearButton}
						disabled={loading || activeCount === 0}
						onClick={handleClearFilters}
						fullWidth
					>
						Clear {activeCount > 0 && `(${activeCount})`}
					</Button>
					<Button
						variant="contained"
						className={classes.applyButton}
						disabled={loading}
						onClick={handleApplyFilter}
						fullWidth
					>
						{loading ? <Spinner animation="border" size="sm"/> : 'Apply Filter'}
					</Button>
				</Box>
			</AccordionWrapper>
		</FilterWrapper>
	);
};

export default React.memo(ProductFilters);